/**
 * Builds a minimal, valid flow document for the "Place a call" panel's quick
 * message mode: type a message, pick a registered voice, skip the designer
 * entirely. Reuses the exact same publish/place machinery every other flow
 * already goes through — this is a document-construction convenience, not a
 * new backend capability.
 */
export interface QuickMessageFlow {
  schema_version: string;
  id: string;
  direction: "inbound" | "outbound";
  start_node_id: string;
  variables: unknown[];
  nodes: Array<{ id: string; type: string; config: Record<string, unknown> }>;
  edges: Array<{ id: string; source: string; port: string; target: string }>;
}

/**
 * amd: when true, inserts an amd_gate node between start and the greeting.
 * voicemailText: when non-empty (and amd is true), MACHINE gets its own
 * separate tts node with this text instead of sharing the human one — the
 * point being to make a wrong AMD classification audible (hearing the
 * "live" message on voicemail, or vice versa) rather than invisible, which
 * is exactly what this checkbox is for: testing AMD accuracy, not yet
 * shipping a production gate/drop decision. Leaving voicemailText empty
 * keeps the original "play the same message either way, just record the
 * result" behavior.
 *
 * NOTSURE routes with human (notsure_policy: "continue_human"). This has
 * been both ways and the second answer came from real calls. It was
 * "hangup" for a while, on the reading that an answered call which says
 * nothing is evidence of nothing, so playing the live message to it was a
 * guess. True, but it made the far more common case worse: a person who
 * answers and speaks quietly, or whose first word lands in the carrier's
 * ~0.9s media cut-through, also produces no detected speech — and they got
 * hung up on mid-"hello". A machine announces itself with a beep on this
 * carrier, every time, within the first window. So "no beep" is the
 * positive signal, and the absence of one means talk to the person.
 *
 * A call that cannot be reached at all — busy, congestion, no answer — never
 * reaches this node: it has no answered leg, so the gate never runs and the
 * attempt is recorded as the connection failure it was.
 *
 * direction must be "outbound" when amd is true — inbound flows are
 * compile-time forbidden from containing an amd_gate node at all
 * (flow.checkAMD) — the plain quick-message flow below keeps "inbound"
 * unchanged for compatibility with calls already placed under it.
 */
export function buildQuickMessageFlow(
  text: string,
  voice: string,
  language: string,
  amd = false,
  voicemailText = "",
): QuickMessageFlow {
  const id = "quick-message-" + crypto.randomUUID();
  if (!amd) {
    return {
      schema_version: "1.0",
      id,
      direction: "inbound",
      start_node_id: "start",
      variables: [],
      nodes: [
        { id: "start", type: "start", config: {} },
        { id: "greet", type: "tts", config: { text, voice, language } },
        { id: "end", type: "hangup", config: { reason: "normal" } },
      ],
      edges: [
        { id: "e1", source: "start", port: "next", target: "greet" },
        { id: "e2", source: "greet", port: "next", target: "end" },
        { id: "e3", source: "greet", port: "error", target: "end" },
      ],
    };
  }

  const hasVoicemailText = voicemailText.trim() !== "";
  const machineTarget = hasVoicemailText ? "greet_machine" : "greet_human";

  return {
    schema_version: "1.0",
    id,
    direction: "outbound",
    start_node_id: "start",
    variables: [],
    nodes: [
      { id: "start", type: "start", config: {} },
      {
        id: "amd",
        type: "amd_gate",
        config: {
          system: true,
          analysis_timeout_ms: 5000,
          notsure_policy: "continue_human",
          beep_wait_ms: 5000,
          no_beep_action: "play_drop",
        },
      },
      { id: "greet_human", type: "tts", config: { text, voice, language } },
      ...(hasVoicemailText
        ? [{ id: "greet_machine", type: "tts", config: { text: voicemailText, voice, language } }]
        : []),
      { id: "end", type: "hangup", config: { reason: "normal" } },
    ],
    edges: [
      { id: "e1", source: "start", port: "next", target: "amd" },
      { id: "e2", source: "amd", port: "human", target: "greet_human" },
      { id: "e3", source: "amd", port: "machine", target: machineTarget },
      // Silence hangs up without playing anything.
      //
      // It used to play the live message. Measured on seven live calls
      // (2026-10-23), an answered call that says nothing produces a
      // capture of pure line noise, and treating that as a person meant
      // talking at a mailbox -- or at someone who had not spoken -- with
      // no evidence either way. A beep is evidence of a machine and
      // speech is evidence of a person; neither is not a reason to play
      // anything.
      { id: "e4", source: "amd", port: "notsure", target: "greet_human" },
      { id: "e5", source: "greet_human", port: "next", target: "end" },
      { id: "e6", source: "greet_human", port: "error", target: "end" },
      ...(hasVoicemailText
        ? [
            { id: "e7", source: "greet_machine", port: "next", target: "end" },
            { id: "e8", source: "greet_machine", port: "error", target: "end" },
          ]
        : []),
    ],
  };
}
